import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { AlignLeft, BrainCircuit, Database, FileCheck, FileText, FlaskConical, LayoutTemplate, RadioTower, Users, Globe } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";


const navItems = [
  { path: "users", label: "Users", description: "Manage application users", icon: Users },
  { path: "broadcast", label: "Broadcast", description: "Publish a system-wide banner message", icon: RadioTower },
  { path: "therapy-areas", label: "Therapy Areas", description: "Manage therapy area classifications", icon: FlaskConical },
  { path: "template-types", label: "Template Types", description: "Manage template type categories", icon: LayoutTemplate },
  { path: "template-format-types", label: "Template Format Types", description: "Manage template format type categories", icon: AlignLeft },
  { path: "prompts", label: "Prompts", description: "Manage AI prompt configurations", icon: BrainCircuit },
  { path: "templates", label: "Templates", description: "Manage templates and prompt libraries", icon: FileText },
  { path: "evidence-types", label: "Evidence Types", description: "Manage evidence type classifications", icon: FileCheck },
  { path: "source-types", label: "Source Types", description: "Manage document source categories", icon: Database },
  { path: "api-configurations", label: "API Configurations", description: "Manage API endpoint configurations", icon: Globe },
];

const Admin = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();

  const projectOwnerAllowedPaths = ['therapy-areas', 'project-codes', 'prompts', 'templates', 'source-types'];
  const visibleNavItems = user?.role === 'Admin'
    ? navItems
    : navItems.filter(item => projectOwnerAllowedPaths.includes(item.path));

  const current = visibleNavItems.find((n) => pathname.endsWith(n.path)) ?? visibleNavItems[0];

  return (
    <PageLayout>
      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
            Master Data
          </p>
          {visibleNavItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>{current.label}</CardTitle>
              <CardDescription>{current.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Outlet />
            </CardContent>
          </Card>
        </main>
      </div>
    </PageLayout>
  );
};

export default Admin;
